import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Key, Plus, Trash2, Copy, ChevronRight, AlertCircle, Loader, X } from 'lucide-react';
import api from '../../services/api';

// Dialog Component
const Dialog = ({ open, onOpenChange, children }) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 dark:bg-black/70"
                onClick={() => onOpenChange(false)}
            />
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/20 max-w-md w-full mx-4 z-50"
            >
                {children}
            </motion.div>
        </div>
    );
};

// Alert Component
const Alert = ({ children, className = "" }) => (
    <div className={`flex items-center gap-2 p-4 text-sm bg-red-50 text-red-600 rounded-lg ${className}`}>
        {children}
    </div>
);

const ApiKeyManagement = () => {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showNewKeyModal, setShowNewKeyModal] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyData, setNewKeyData] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        fetchKeys();
    }, []);

    useEffect(() => {
        console.log('Current keys:', keys);
    }, [keys]);

    const fetchKeys = async () => {
        try {
            const response = await api.get('/api/api-keys/');
            console.log('API Response:', response.data);  
            setKeys(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch API keys');
            console.error('Error fetching keys:', err);
        } finally {
            setLoading(false);
        }
    };

    const createKey = async () => {
        try {
            const response = await api.post('/api/api-keys/create/', {
                name: newKeyName || undefined
            });
            setNewKeyData(response.data);
            await fetchKeys();
            setNewKeyName('');
        } catch (err) {
            setError('Failed to create API key');
            console.error('Error creating key:', err);
        }
    };

    const deleteKey = async (id) => {
        setDeletingId(id);
        console.log('Deleting key with ID:', id); 
        try {
            await api.delete(`/api/api-keys/${id}/delete/`);
            await fetchKeys();
        } catch (err) {
            console.error('Error response:', err.response);
            setError('Failed to delete API key');
            console.error('Error deleting key:', err);
        } finally {
            setDeletingId(null);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
                <Loader className="w-8 h-8 text-primary-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8"
                >
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-start gap-3">
                            <Key className="w-8 h-8 text-primary-600 mt-2" />
                            <h1 className="text-3xl font-bold text-gray-900 !mb-0">API Keys</h1>
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                                setShowNewKeyModal(true);
                                setNewKeyData(null);
                            }}
                            className="bg-primary-600 text-white px-4 py-2 rounded-full hover:bg-primary-700 
                            transition-colors duration-300 flex items-center shadow-sm dark:shadow-gray-900/20"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Create New Key
                        </motion.button>
                    </div>

                    {error && (
                        <Alert className="mb-6">
                            <AlertCircle className="h-4 w-4" />
                            <span>{error}</span>
                        </Alert>
                    )}

                    <AnimatePresence>
                        {keys.length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900/20 border border-gray-100 dark:border-gray-700"
                            >
                                <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No API Keys Found</h3>
                                <p className="text-gray-500">Create your first API key to get started</p>
                            </motion.div>
                        ) : (
                            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900/20 border border-gray-100 dark:border-gray-700 overflow-hidden">
                                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                {keys.map((key, index) => (
                            <motion.div
                                key={key.id}  // Use the shorter id (prefix) for the React key
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                            >
                                <div className="flex items-center space-x-4">
                                    <div className="bg-primary-50 p-2 rounded-full">
                                        <Key className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900">{key.name}</h3>
                                        <p className="text-sm text-gray-500">Created {formatDate(key.created)}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    {deletingId === key.id ? (  // Use the same id (prefix) here
                                        <Loader className="w-5 h-5 text-gray-400 animate-spin" />
                                    ) : (
                                        <motion.button
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            onClick={() => deleteKey(key.id)}  // Use the id (prefix) for deletion
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </motion.button>
                                    )}
                                    <ChevronRight className="w-5 h-5 text-gray-300" />
                                </div>
                            </motion.div>
                        ))}
                        </div>
                    </div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>

            <Dialog open={showNewKeyModal} onOpenChange={setShowNewKeyModal}>
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">Create New API Key</h2>
                        <button
                            onClick={() => setShowNewKeyModal(false)}
                            className="text-gray-400 hover:text-gray-500 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <p className="text-sm text-gray-500 mb-6">
                        Enter a name for your new API key. This will help you identify its usage later.
                    </p>

                    {!newKeyData ? (
                        <div className="space-y-4">
                            <input
                                type="text"
                                placeholder="API Key Name"
                                value={newKeyName}
                                onChange={(e) => setNewKeyName(e.target.value)}
                                className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 
                                focus:ring-primary-500 focus:border-transparent transition-shadow duration-200"
                            />
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowNewKeyModal(false)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={createKey}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 
                                    transition-colors duration-200"
                                >
                                    Create Key
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg break-all">
                                <p className="text-sm font-mono text-gray-800">{newKeyData.key}</p>
                            </div>
                            <div className="flex justify-between items-center">
                                <p className="text-sm text-amber-600">
                                    Make sure to copy your API key now. You won't be able to see it again!
                                </p>
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => copyToClipboard(newKeyData.key)}
                                    className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white 
                                    rounded-lg hover:bg-primary-700 transition-colors duration-200"
                                >
                                    <Copy className="w-4 h-4" />
                                    <span>{copySuccess ? 'Copied!' : 'Copy'}</span>
                                </motion.button>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
};

export default ApiKeyManagement;